import React, { useEffect, useRef } from 'react';

const carIcon = process.env.PUBLIC_URL + '/vehicles/car-top-view.svg';
const motoIcon = process.env.PUBLIC_URL + '/vehicles/moto.svg';
const vanIcon = process.env.PUBLIC_URL + '/vehicles/caminhonete-bau.svg';
const pickupIcon = process.env.PUBLIC_URL + '/vehicles/utilitario-caminhonete.svg';
const busIcon = process.env.PUBLIC_URL + '/vehicles/onibus.svg';

// ── MALHA VIÁRIA COMO GRAFO ──────────────────────────────────────────────────
// 4 nós na rotatória (N/L/S/O). Cada trecho ("edge") liga um nó a outro. Onde
// existem 2 trechos entre os mesmos nós (N→L e S→O), o veículo escolhe um
// aleatoriamente ao chegar — é aí que a rota vira imprevisível.
const TRECHOS = {
  // Rotatória — 4 arcos de 1/4 de volta, o caminho "curto" entre nós vizinhos
  arcoNL: { de: 'N', para: 'L', d: 'M240,188 A42,42 0 0 1 282,230' },
  arcoLS: { de: 'L', para: 'S', d: 'M282,230 A42,42 0 0 1 240,272' },
  arcoSO: { de: 'S', para: 'O', d: 'M240,272 A42,42 0 0 1 198,230' },
  arcoON: { de: 'O', para: 'N', d: 'M198,230 A42,42 0 0 1 240,188' },
  // Avenida — o caminho "longo", saindo da rotatória, passando pelo cruzamento
  // com PARE, e voltando a se conectar à rotatória em outro ponto.
  avenidaNL: { de: 'N', para: 'L', d: 'M240,188 C240,150 240,112 240,95 C240,55 320,40 370,90 C410,130 400,190 282,230' },
  avenidaSO: { de: 'S', para: 'O', d: 'M240,272 C240,320 240,360 240,375 C240,410 160,420 110,375 C70,340 80,280 198,230' },
};

// Rua secundária — não faz parte do grafo da rotatória, mas cruza a
// "avenidaNL" perto de (240,95). É exatamente ali que fica o cruzamento com PARE.
const RUA_SECUNDARIA = 'M140,95 C140,60 340,60 340,150 C340,200 240,200 240,150 C240,120 180,95 140,95 Z';
const PONTO_PARE = { x: 240, y: 95 };
const RAIO_ZONA_PARE = 34;

// Cada veículo nasce numa rota (grafo, entrando por N ou S) ou na rua
// secundária (loop próprio). `edgeAtual` é reavaliado a cada nó — por isso o
// percurso muda a cada volta.
const VEICULOS_CONFIG = [
  { grupo: 'grafo', noInicial: 'N', icone: carIcon, tamanho: 28, velCruzeiro: 46 },
  { grupo: 'grafo', noInicial: 'L', icone: carIcon, tamanho: 28, velCruzeiro: 40 },
  { grupo: 'grafo', noInicial: 'S', icone: busIcon, tamanho: 33, velCruzeiro: 28 },
  { grupo: 'grafo', noInicial: 'O', icone: pickupIcon, tamanho: 28, velCruzeiro: 42 },
  { grupo: 'secundaria', icone: motoIcon, tamanho: 29, velCruzeiro: 50, inicio: 0.15 },
  { grupo: 'secundaria', icone: vanIcon, tamanho: 28, velCruzeiro: 32, inicio: 0.6 },
];

const DIST_MINIMA = 28;
const ACELERACAO = 55;
const FREADA = 100;

// Escolhe aleatoriamente, entre os trechos que partem de um nó, qual seguir.
function escolherProximoTrecho(no) {
  const opcoes = Object.entries(TRECHOS).filter(([, t]) => t.de === no);
  return opcoes[Math.floor(Math.random() * opcoes.length)][0];
}

export default function TrafficMap() {
  const pathRefs = useRef({});
  const gRefs = useRef([]);
  const veiculosRef = useRef([]);
  const frameRef = useRef(null);
  const ultimoTempoRef = useRef(null);
  const zonaParadaRef = useRef({ ocupante: null });

  useEffect(() => {
    // Comprimento de cada trecho do grafo + a rua secundária
    const comprimentos = {};
    Object.keys(TRECHOS).forEach((chave) => {
      comprimentos[chave] = pathRefs.current[chave].getTotalLength();
    });
    const compSecundaria = pathRefs.current.secundaria.getTotalLength();

    // Acha, por amostragem, a que distância de cada via fica o ponto do PARE
    // (evita ter que calcular isso à mão pra cada curva Bézier).
    function distanciaMaisProxima(path, alvo) {
      const total = path.getTotalLength();
      let melhor = 0, menorDist = Infinity;
      for (let i = 0; i <= 200; i++) {
        const d = (i / 200) * total;
        const p = path.getPointAtLength(d);
        const dist = (p.x - alvo.x) ** 2 + (p.y - alvo.y) ** 2;
        if (dist < menorDist) { menorDist = dist; melhor = d; }
      }
      return melhor;
    }
    const paradaAvenidaNL = distanciaMaisProxima(pathRefs.current.avenidaNL, PONTO_PARE);
    const paradaSecundaria = distanciaMaisProxima(pathRefs.current.secundaria, PONTO_PARE);

    veiculosRef.current = VEICULOS_CONFIG.map((v) => {
      if (v.grupo === 'secundaria') {
        return { ...v, distancia: v.inicio * compSecundaria, velocidade: v.velCruzeiro, comprimentoRota: compSecundaria };
      }
      const edgeAtual = escolherProximoTrecho(v.noInicial);
      return { ...v, edgeAtual, distancia: 0, velocidade: v.velCruzeiro };
    });

    const animar = (tempoAtual) => {
      if (ultimoTempoRef.current == null) ultimoTempoRef.current = tempoAtual;
      const dt = Math.min((tempoAtual - ultimoTempoRef.current) / 1000, 0.05);
      ultimoTempoRef.current = tempoAtual;

      const veiculos = veiculosRef.current;

      // ── Distância mínima entre veículos no MESMO trecho no momento ──────
      const porTrecho = {};
      veiculos.forEach((v, i) => {
        const chave = v.grupo === 'secundaria' ? 'secundaria' : v.edgeAtual;
        (porTrecho[chave] = porTrecho[chave] || []).push(i);
      });

      const alvoVelocidade = veiculos.map((v) => v.velCruzeiro);

      Object.values(porTrecho).forEach((indices) => {
        indices.sort((a, b) => veiculos[a].distancia - veiculos[b].distancia);
        for (let k = 0; k < indices.length - 1; k++) {
          const atras = veiculos[indices[k]];
          const frente = veiculos[indices[k + 1]];
          const gap = frente.distancia - atras.distancia;
          if (gap < DIST_MINIMA * 2.5) {
            const fator = Math.max(0, (gap - DIST_MINIMA) / (DIST_MINIMA * 1.5));
            alvoVelocidade[indices[k]] = Math.min(alvoVelocidade[indices[k]], atras.velCruzeiro * fator);
          }
        }
      });

      // ── Cruzamento com PARE: quem chega primeiro tem prioridade ─────────
      const zona = zonaParadaRef.current;
      const emZona = { avenidaNL: null, secundaria: null };
      veiculos.forEach((v, i) => {
        if (v.grupo === 'grafo' && v.edgeAtual === 'avenidaNL') {
          if (Math.abs(v.distancia - paradaAvenidaNL) < RAIO_ZONA_PARE) emZona.avenidaNL = i;
        }
        if (v.grupo === 'secundaria') {
          if (Math.abs(v.distancia - paradaSecundaria) < RAIO_ZONA_PARE) emZona.secundaria = i;
        }
      });

      if (emZona.avenidaNL != null && emZona.secundaria == null) zona.ocupante = 'avenidaNL';
      else if (emZona.secundaria != null && emZona.avenidaNL == null) zona.ocupante = 'secundaria';
      else if (emZona.avenidaNL == null && emZona.secundaria == null) zona.ocupante = null;
      // se os dois estão na zona ao mesmo tempo, mantém quem já tinha prioridade

      if (emZona.avenidaNL != null && zona.ocupante === 'secundaria') alvoVelocidade[emZona.avenidaNL] = 0;
      if (emZona.secundaria != null && zona.ocupante === 'avenidaNL') alvoVelocidade[emZona.secundaria] = 0;

      // ── Aplica aceleração/frenagem suave em direção à velocidade-alvo ───
      veiculos.forEach((v, i) => {
        const alvo = alvoVelocidade[i];
        if (v.velocidade < alvo) v.velocidade = Math.min(alvo, v.velocidade + ACELERACAO * dt);
        else if (v.velocidade > alvo) v.velocidade = Math.max(alvo, v.velocidade - FREADA * dt);
      });

      // ── Avança cada veículo e, se chegou ao fim do trecho, escolhe o próximo ─
      veiculos.forEach((v, i) => {
        if (v.grupo === 'secundaria') {
          v.distancia = (v.distancia + v.velocidade * dt) % v.comprimentoRota;
          if (v.distancia < 0) v.distancia += v.comprimentoRota;
        } else {
          const comp = comprimentos[v.edgeAtual];
          v.distancia += v.velocidade * dt;
          if (v.distancia >= comp) {
            v.distancia -= comp;
            const noChegada = TRECHOS[v.edgeAtual].para;
            v.edgeAtual = escolherProximoTrecho(noChegada);
          }
        }

        const path = v.grupo === 'secundaria' ? pathRefs.current.secundaria : pathRefs.current[v.edgeAtual];
        const comp2 = v.grupo === 'secundaria' ? v.comprimentoRota : comprimentos[v.edgeAtual];
        const p1 = path.getPointAtLength(v.distancia);
        const p2 = path.getPointAtLength(Math.min(v.distancia + 1.2, comp2));
        const angulo = Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);

        const g = gRefs.current[i];
        if (g) g.setAttribute('transform', `translate(${p1.x.toFixed(2)},${p1.y.toFixed(2)}) rotate(${angulo.toFixed(1)})`);
      });

      frameRef.current = requestAnimationFrame(animar);
    };

    frameRef.current = requestAnimationFrame(animar);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      ultimoTempoRef.current = null;
    };
  }, []);

  return (
    <div className="map-card" aria-hidden="true">
      <svg viewBox="0 0 480 480" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="glowNivocar" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="4" result="blur1" />
            <feMerge>
              <feMergeNode in="blur1" /><feMergeNode in="blur1" /><feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="glowFundoRotatoria" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(59,130,246,0.35)" />
            <stop offset="100%" stopColor="rgba(59,130,246,0)" />
          </radialGradient>
        </defs>

        <g className="map-grid">
          {[80, 160, 240, 320, 400].map((v) => (
            <React.Fragment key={v}>
              <line x1={v} y1="0" x2={v} y2="480" />
              <line x1="0" y1={v} x2="480" y2={v} />
            </React.Fragment>
          ))}
        </g>

        {/* Vias — borda + asfalto + faixa central */}
        {Object.entries(TRECHOS).map(([chave, t]) => (
          <path key={`edge-${chave}`} d={t.d} className="road-edge" />
        ))}
        <path d={RUA_SECUNDARIA} className="road-edge" />
        {Object.entries(TRECHOS).map(([chave, t]) => (
          <path key={`surf-${chave}`} d={t.d} className="road-surface" />
        ))}
        <path d={RUA_SECUNDARIA} className="road-surface" />
        {Object.entries(TRECHOS).map(([chave, t]) => (
          <path
            key={`center-${chave}`}
            ref={(el) => (pathRefs.current[chave] = el)}
            d={t.d}
            className={chave.startsWith('arco') ? 'road-centerline-rotatoria' : 'road-centerline'}
          />
        ))}
        <path ref={(el) => (pathRefs.current.secundaria = el)} d={RUA_SECUNDARIA} className="road-centerline" />

        {/* Sinalização de PARE */}
        <g transform={`translate(${PONTO_PARE.x - 26},${PONTO_PARE.y - 4})`}>
          <polygon points="8,0 20,0 26,6 26,18 20,24 8,24 2,18 2,6" className="sinal-pare-fundo" />
          <text x="14" y="16" className="sinal-pare-texto" textAnchor="middle">PARE</text>
        </g>

        {/* Rotatória — brilho radial + ilha central com o nome */}
        <circle cx="240" cy="230" r="70" fill="url(#glowFundoRotatoria)" />
        <circle className="roundabout-hub" cx="240" cy="230" r="26" />
        <text className="rotatoria-texto" x="240" y="235" textAnchor="middle" filter="url(#glowNivocar)">NIVOCAR</text>

        {/* Veículos */}
        {VEICULOS_CONFIG.map((v, i) => (
          <g key={i} ref={(el) => (gRefs.current[i] = el)}>
            <g transform="rotate(90)">
              <image
                href={v.icone}
                x={-v.tamanho / 2}
                y={-v.tamanho / 2}
                width={v.tamanho}
                height={v.tamanho}
                style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.55))' }}
              />
            </g>
          </g>
        ))}
      </svg>
    </div>
  );
}
